import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { SegmentLabels } from '@se/cp.common';
import { IAnatomicMap } from '../../models/IAnatomicMap';
import { IClickPoint } from '../../models/IClickPoint';
import { IColorData } from '../../models/IColorData';
import { IPoint } from '../../models/IPoint';
import { ISegmentItem } from '../../models/ISegmentItem';
import { TextType } from '../../models/TextTypeEnum';
import { BrightnessDetectorService } from '../brightnessDetectorService/brightnessDetector.service';

import { DomSubsService } from '../domSubsService/domSubs.service';

export interface ISvgTextBorder {
    strokeColor: string;
    strokeWidth: string;
    paintOrder: string
}

@Injectable({ providedIn: 'root' })
export class SvgShapeService {
  private r: Renderer2;

  constructor(rendererFactory: RendererFactory2, private domSubsService: DomSubsService, private brighnessService: BrightnessDetectorService) {
    this.r = rendererFactory.createRenderer(null, null);
  }

  public drawClickPoint(p: IClickPoint): SVGCircleElement {
    const circle = this.createSvgHtmlEl('circle') as SVGCircleElement;
    this.r.setAttribute(circle, 'stroke', 'red');
    this.r.setAttribute(circle, 'r', '15');
    this.r.setAttribute(circle, 'stroke-width', '2');
    this.r.setAttribute(circle, 'fill', 'none');
    this.r.setAttribute(circle, 'cx', p.coo.x.toString());
    this.r.setAttribute(circle, 'cy', p.coo.y.toString());
    this.r.setAttribute(circle, 'fatherId', p.fatherId);
    this.r.setAttribute(circle, 'id', 'CLICK_POINT');
    return circle;
  }

  public addListener(
    element: HTMLOrSVGElement,
    evtName: string,
    callback: (ev: MouseEvent) => void
  ): HTMLOrSVGElement {
    const listenerFn = this.r.listen(element, evtName, (ev) => callback(ev));
    this.domSubsService.addSub({
      listenerFn,
      evtName,
      originEl: element,
    });
    return element;
  }

  public appendChild(father: HTMLOrSVGElement, son: HTMLOrSVGElement): HTMLOrSVGElement {
    this.r.appendChild(father, son);
    return father;
  }

  public removeChild(father: HTMLOrSVGElement, son: HTMLOrSVGElement): void {
    this.r.removeChild(father, son);
  }

  public drawSegment(segment: ISegmentItem, fill: IColorData, d: string): SVGPathElement {
    const path = this.createSvgHtmlEl('path') as SVGPathElement;
    this.r.setAttribute(path, 'stroke', segment.isVisited ? 'cyan' : 'black');
    this.r.setAttribute(path, 'stroke-width', '2');
    this.r.setAttribute(path, 'fill', this.getColorCodeStr(fill));
    this.r.setAttribute(path, 'id', segment.regionID);
    this.r.setAttribute(path, 'value', segment.value?.toString());
    this.r.setAttribute(path, 'pointer-events', 'painted');
    this.r.setAttribute(path, 'd', d);
    return path;
  }

  public drawGroup(groupId: string): SVGGElement {
    const g = this.createSvgHtmlEl('g') as SVGGElement;
    const id = groupId ? groupId : 'map';
    this.r.setAttribute(g, 'id', id);
    return g;
  }

  public drawSegmentLabel(
    seg: ISegmentItem,
    fill: IColorData,
    type: SegmentLabels,
    isVisited? :boolean
  ): SVGTextElement {
    const position = this.getPosition(seg);

    const txt = this.r.createText(this.getLabelText(seg, fill, type)) as SVGTextElement;
    let textFill: string;
    let textStroke: string;
    let strokeWidth: string;
    const paintOrder= 'stroke';
    const isTextLabel = type === SegmentLabels.label;
    if(this.brighnessService.darkOrLight(fill?.color) === 'light'){
        textFill='black';
        textStroke = isTextLabel ? 'white': '';
        strokeWidth = isTextLabel ? '3px' : '0px';
    }else{
        textFill ='white';
        textStroke=  textStroke = isTextLabel ? 'black': '';
        strokeWidth = isTextLabel ? '3px' : '0px';
    }
    const textBorder: ISvgTextBorder = { strokeColor: textStroke, strokeWidth: strokeWidth, paintOrder: paintOrder };
    return  this.createTextEl(position, textFill, txt, TextType.label, textBorder, isVisited);

  }

  public drawTooltip(segment: ISegmentItem, data: IColorData): SVGGElement {
    const position = this.getPosition(segment),
      text1 = this.createTextEl(position, 'black', this.r.createText(data.segmentDescription || 'No Description' ), TextType.tooltip),
      text2 = this.createTextEl(position, 'black', this.r.createText(data.description), TextType.tooltip),
      rect = this.createRect(position, 0, 0, 4),
      g = this.createSvgHtmlEl('g') as SVGGElement;
    this.r.appendChild(g, rect);
    this.r.appendChild(g, text1);
    this.r.appendChild(g, text2);
    this.r.setStyle(g, 'pointer-events', 'none');
    this.r.setAttribute(g, 'id', TextType.tooltip);
    return g;
  }

  public setElStyle(el: HTMLOrSVGElement, style: string, value: string) {
    this.r.setStyle(el, style , value);
  }

  public correctTooltip(tooltip: SVGGElement, viewBoxXY: IPoint, tooltipPadding: number = 10, interline: number = 3): void {
    const rect = tooltip.children[0] as SVGRectElement,
      textRow1 = tooltip.children[1] as SVGTextElement,
      text1Bbox = textRow1.getBBox(),
      textRow2 = tooltip.children[2] as SVGTextElement,
      text2Bbox = textRow2.getBBox(),
      domintantWidth = (text1Bbox.width > text2Bbox.width ? text1Bbox : text2Bbox).width,
      textPosition: IPoint = {x: this.getXOrY(textRow1, 'x'), y: this.getXOrY(textRow2, 'y') + interline + text1Bbox.height };
    const newRow2 = this.createTextEl(textPosition, 'black', this.r.createText(textRow2.textContent), TextType.tooltip)
    const newRect = this.createRect(
      { x: text1Bbox.x - tooltipPadding, y: text1Bbox.y - tooltipPadding },
      domintantWidth + tooltipPadding * 2,
      text1Bbox.height + text2Bbox.height + tooltipPadding * 2,
      4
    ) as SVGRectElement;
    textRow2.replaceWith(newRow2)
    rect.replaceWith(newRect);
    this.traslateTooltip(tooltip, viewBoxXY);
  }

  private createTextEl(
    position: IPoint,
    fillColor: string,
    textContent: any,
    type: TextType,
    textBorder?: ISvgTextBorder,
    isVisited?: boolean
  ): SVGTextElement {
    const text = this.createSvgHtmlEl('text') as SVGTextElement;

    this.r.setAttribute(text, 'x', position.x.toString());
    this.r.setAttribute(text, 'y', position.y.toString());
    this.r.setAttribute(text, 'fill', fillColor);
    this.r.setAttribute(text, 'id', type);
    this.r.setAttribute(text, 'dominant-baseline', 'hanging');
    if(textBorder){
        this.r.setAttribute(text, 'stroke', textBorder.strokeColor);
        this.r.setAttribute(text, 'stroke-width', textBorder.strokeWidth);
        this.r.setAttribute(text, 'paint-order', textBorder.paintOrder);
    }
    if(type === TextType.label) {
        this.r.setAttribute(text, 'text-anchor', 'middle');
    }
    if(isVisited){
        this.r.setAttribute(text, 'font-size', '1.2em');
    }
    this.r.setStyle(text, 'pointer-events', 'none');
    this.r.appendChild(text, textContent);
    return text;
  }

  private createRect(
    position: IPoint,
    width: number,
    height: number,
    cornerRadius?: number
  ): SVGRectElement {
    const rect = this.createSvgHtmlEl('rect') as SVGRectElement;
    this.r.setAttribute(rect, 'width', width.toString());
    this.r.setAttribute(rect, 'height', height.toString());
    this.r.setAttribute(rect, 'fill', '#f5f5f5');
    this.r.setAttribute(rect, 'stroke', '#c3c3c3');
    this.r.setAttribute(rect, 'stroke-width', '1');
    this.r.setAttribute(rect, 'x', position.x.toString());
    this.r.setAttribute(rect, 'y', position.y.toString());
    this.r.setAttribute(rect,'pointer-event','painted')
    if (cornerRadius) {
      this.r.setAttribute(rect, 'rx', cornerRadius + 'px');
    }
    return rect;
  }

  public createCircle(position: IPoint): SVGCircleElement {
    const c = this.createSvgHtmlEl('circle') as SVGCircleElement;
    this.r.setAttribute(c, 'cx', '' + position.x);
    this.r.setAttribute(c, 'cy', '' + position.y);
    this.r.setAttribute(c, 'fill', 'none');
    this.r.setAttribute(c, 'stroke', 'magenta');
    this.r.setAttribute(c, 'stroke-width', '1');
    this.r.setAttribute(c, 'r', '5');
    this.r.setAttribute(c,'pointer-event','painted')
    return c;
  }

  private getLabelText(seg: ISegmentItem, fill: IColorData, type: SegmentLabels): string {
    switch (type) {
      case SegmentLabels.alphaCode:
        return fill?.alphaCode;
      case SegmentLabels?.value:
        return fill?.score?.toString();
      case SegmentLabels.label:
        return seg?.regionID
      case SegmentLabels.none:
      default:
        return '';
    }
  }

  private createSvgHtmlEl(elName: string): HTMLOrSVGElement {
    return this.r.createElement(elName, 'svg');
  }

  private getColorCodeStr(code: IColorData): string {
    return !code || code === undefined ? 'white' : code.color === null ? 'white' : code.color;
  }

  private getPosition(seg: ISegmentItem):IPoint {
    return seg.internalPoints ? seg.internalPoints
                : seg.startPoint ? seg.startPoint
                    : 'radius' in seg.pieces[0] ? seg.pieces[0].start
                        : seg.pieces[0].points[0];
  }




  private traslateTooltip(tooltip: SVGGElement, viewBoxXY: IPoint, borderPadding = 10) {
    let translateX = 0,
        translateY = 0,
        transform = false;
    const tooltipBase = tooltip.children[0],
        actualWidth = this.getFromAnimatedLenght(tooltipBase['width']),
        actualHeight = this.getFromAnimatedLenght(tooltipBase['height']),
        actualx = this.getFromAnimatedLenght(tooltipBase['x']),
        actualy = this.getFromAnimatedLenght(tooltipBase['y']),
        deltaX = viewBoxXY.x - (actualx + actualWidth),
        deltaY = viewBoxXY.y - (actualy + actualHeight);
    if (deltaX < 0) {
        translateX = deltaX - borderPadding;
        transform = true
    }
    if (deltaY < 0) {
        translateY = deltaY - borderPadding;
        transform = true
    }
    if(transform) {
      this.r.setAttribute(tooltip, 'transform', `translate(${translateX} ${translateY})`);
    }
  }


  private getXOrY(element:SVGElement, xOrY: 'x' | 'y') {
    return element[xOrY].baseVal["0"].value
  }

  private getFromAnimatedLenght(length: SVGAnimatedLength) {
    return length.baseVal.value
  }
}
